import React, { useState, useEffect } from "react";
import clsx from "clsx";

const DynamicTable = ({
  columns,
  data = [],
  selectedRow,
  onRowSelect,
  rowKey = "id",
  noDataMessage = "No data available",
  highlightColor = "bg-blue-100",
  hoverColor = "hover:bg-gray-50",
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  searchQuery = "",
}) => {
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = data.filter((row) => {
        return Object.values(row).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [data, searchQuery]);

  const renderPaginationButtons = () => {
    if (!onPageChange || !totalPages) return null;

    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={clsx(
            "px-3 py-1 mx-1 rounded",
            currentPage === i
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          )}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100 text-gray-800">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="border border-gray-300 px-6 py-3 text-left text-lg font-semibold"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.map((row) => (
              <tr
                key={row[rowKey]}
                className={clsx(
                  "cursor-pointer",
                  selectedRow?.[rowKey] === row[rowKey] ? highlightColor : "",
                  hoverColor,
                  "border-b border-gray-300"
                )}
                onClick={() => onRowSelect(row)}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="border border-gray-300 px-5 py-3 text-base"
                  >
                    {row[column.key]}
                  </td>
                ))}
              </tr>
            ))}

            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border px-4 py-2 text-center text-gray-500"
                >
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {onPageChange && totalPages && (
        <div className="flex justify-center items-center mt-4">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={clsx(
              "px-3 py-1 mx-1 rounded",
              currentPage === 1
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-gray-200 hover:bg-gray-300"
            )}
          >
            Previous
          </button>
          {renderPaginationButtons()}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={clsx(
              "px-3 py-1 mx-1 rounded",
              currentPage === totalPages
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-gray-200 hover:bg-gray-300"
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
